import {
  Button,
  FieldLabel,
  FieldValue,
  Icon,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import React, { FunctionComponent, useCallback, useState } from 'react'
import { ApiCustomer } from '@vend/types'
import { CustomerBadge } from '@vend/react-utilities'
import { getCustomerNameFromApiCustomer } from '@vend/react-utilities/dist/components/badges/customerBadge/CustomerBadge'
import { CustomerSearch } from '../customer/customer-search'
import { UpdateSaleCustomerModal } from '../../modals/update-customer-modal'
import {
  useRetailerSettingsManager,
  useSaleManager,
} from '../../ng-compat/hooks'
import { CustomerBalances } from '../../models/sale'
import { customerBalancesManager } from '../../managers/customer-balances-manager'
import { useFormatCurrencyValue } from './summary-panel/useFormatCurrencyValue'

interface AddCustomerModalProps {
  onClose: () => void
}

const CustomerBalancesDisplay: FunctionComponent<{
  balances: CustomerBalances
}> = ({ balances }) => {
  const retailerSettingsManager = useRetailerSettingsManager()
  const formatCurrency = useFormatCurrencyValue()

  const loyaltyEnabled = retailerSettingsManager.isLoyaltyEnabled()
  const storeCreditEnabled = retailerSettingsManager.isStoreCreditEnabled()

  return (
    <ul className="wr-info-fields wr-info-fields--large wr-info-fields--center vd-mb3">
      <li className="wr-info-field">
        <div className="wr-info-field-label">
          <T _str="Account Balance" />
        </div>
        <div className="wr-info-field-value pro-customer-balances-amount--account">
          {formatCurrency(balances.account)}
          {balances.onAccountLimit !== null ? (
            <Text tag="p" color="supplementary">
              <T
                _str="On account limit available: {limit}"
                limit={
                  balances.availableOnAccountLimitBalance < 0
                    ? 0
                    : formatCurrency(balances.availableOnAccountLimitBalance)
                }
              />
            </Text>
          ) : null}
        </div>
      </li>

      {loyaltyEnabled ? (
        <li className="wr-info-field">
          <div className="wr-info-field-label">
            <T _str="Loyalty" />
          </div>
          <div className="wr-info-field-value wr-customer-loyalty-balance wr-customer-loyalty-balance--disabled">
            {formatCurrency(balances.loyalty)}
          </div>
        </li>
      ) : null}

      {storeCreditEnabled ? (
        <li className="wr-info-field">
          <div className="wr-info-field-label">
            <T _str="Store Credit" />
          </div>
          <div className="wr-info-field-value pro-customer-balances-amount--store-credit">
            {balances.storeCredit.balance === null ? (
              <Icon
                icon="fa-exclamation-triangle"
                className="wr-customer-balances-error-icon"
              />
            ) : (
              formatCurrency(balances.storeCredit.balance)
            )}
          </div>
        </li>
      ) : null}
    </ul>
  )
}

export const AddCustomerModal: FunctionComponent<AddCustomerModalProps> = ({
  onClose,
}) => {
  const t = useT()
  const saleManager = useSaleManager()
  const [selectedCustomer, setSelectedCustomer] = useState<ApiCustomer | null>(
    null
  )
  const [
    customerBalances,
    setCustomerBalances,
  ] = useState<CustomerBalances | null>(null)
  const [addUser, setAddUser] = useState<boolean>(false)
  const [query, setQuery] = useState<string>('')

  const customerSelected = useCallback(async (customer: ApiCustomer | null) => {
    if (!customer) {
      setSelectedCustomer(null)
      return
    }
    setSelectedCustomer(customer)
    setQuery(getCustomerNameFromApiCustomer(customer) || '')
    const balances = await customerBalancesManager.getCustomerBalances(customer)
    setCustomerBalances(balances)
  }, [])

  const addCustomerToSale = useCallback(() => {
    if (!selectedCustomer) return
    saleManager.addCustomer(selectedCustomer)
    saleManager.saveSale()
    onClose()
  }, [selectedCustomer, saleManager, onClose])

  if (addUser) {
    return (
      <UpdateSaleCustomerModal onClose={onClose} customerSearchQuery={query} />
    )
  }

  return (
    <Modal onClose={onClose}>
      <ModalHeader>
        <T _str="Add a customer to the return sale" />
      </ModalHeader>
      <ModalContent className="wr-customer-search-modal-content">
        <div className="vd-field">
          <FieldLabel label={'Add a customer'} />
          <FieldValue>
            <CustomerSearch
              onSelect={customerSelected}
              onAdd={newCustomerQuery => {
                setQuery(newCustomerQuery || '')
                setAddUser(true)
              }}
              onFetch={setQuery}
              searchValue={query}
              placeholder={t('Enter a customer name')}
            />
          </FieldValue>
        </div>
        {selectedCustomer && (
          <div className="vd-mt10 wr-customer-badge">
            <div className="wr-customer-badge-content vd-pt4 vd-pb2 vd-pl4 vd-pr4">
              <CustomerBadge size="x-small" apiCustomer={selectedCustomer} />
            </div>
            {customerBalances ? (
              <div className="wr-customer-badge-balances">
                <CustomerBalancesDisplay balances={customerBalances} />
              </div>
            ) : null}
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <div className="btn-group vd-align-right">
          <Button variant="supplementary" onClick={onClose}>
            <T _str="Skip" _context="Skip adding customer to sale" />
          </Button>
          <Button className="vd-ml2" onClick={addCustomerToSale}>
            <T _str="Add customer to sale" />
          </Button>
        </div>
      </ModalActions>
    </Modal>
  )
}
